// utils/intel.js
// Shared plumbing for the Intel module (Beta).

import { readApiJson } from '@/utils/apiHelpers';

export const INTEL_API = '/api/portal/intel';

/**
 * Intel is gated to this list. Note it is a THIRD hardcoded list, distinct
 * from both the two-ID UAS gate and the six-ID portal super-admin set — do not
 * merge them. Kept hardcoded to match the Blazor app.
 */
export const INTEL_USER_IDS = [
    '5555555@internal.fire.lacounty.gov',
    '2222222@internal.fire.lacounty.gov',
    '1111111@internal.fire.lacounty.gov',
    '3333333@internal.fire.lacounty.gov',
    '0000000@internal.fire.lacounty.gov',
    'hans.christian@fire.lacounty.gov',
    'lyle.koegler@fire.lacounty.gov',
    '451786@internal.fire.lacounty.gov',
];

export function isIntelUser(user) {
    return !!user?.id && INTEL_USER_IDS.includes(user.id);
}

/** POST an Intel endpoint. `Key`/`OrgId` are stamped automatically. */
export async function intelPost(endpoint, { authKey, orgId, ...body } = {}, options = {}) {
    const res = await fetch(`${INTEL_API}/${endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ Key: authKey, OrgId: orgId, ...body }),
        signal: options.signal,
    });
    if (!res.ok) throw new Error(`Request failed (${res.status})`);
    return readApiJson(await res.json());
}

// ── Enums ────────────────────────────────────────────────────────────────────
export const SOURCE_TYPES = [
    'RSS', 'YouTubeChannel', 'GoogleNews', 'WebPage', 'Podcast', 'AudioBlog', 'Video', 'Manual',
];

export const CARD_SOURCE_TYPES = ['RSS', 'YouTube', 'WebSearch', 'Podcast', 'Manual'];

export const DETAIL_LEVELS = ['Small', 'Medium', 'Large'];

/** The 3-card detail-level selector (IntelAdmin.razor L472-485 / L862-875). */
export const DETAIL_LEVEL_OPTIONS = [
    { value: 'Small', description: '2-3 sentence summary, 1-2 sentence impacts' },
    { value: 'Medium', description: '4-6 sentence summary, 2-3 sentence impacts' },
    { value: 'Large', description: '2-3 paragraph summary, paragraph-length impacts' },
];

/** Dropdown options on the inline add-source forms (IntelAdmin.razor L282-287, L719-725). */
export const WIZARD_ADD_SOURCE_TYPES = [
    { value: 'RSS', label: 'RSS' },
    { value: 'GoogleNews', label: 'Google News' },
    { value: 'YouTubeChannel', label: 'YouTube' },
    { value: 'Podcast', label: 'Podcast' },
];
export const EDIT_ADD_SOURCE_TYPES = [...WIZARD_ADD_SOURCE_TYPES, { value: 'WebPage', label: 'Web Page' }];

const SOURCE_TYPE_LABELS = {
    YouTubeChannel: 'YouTube Channel',
    GoogleNews: 'Google News',
    WebPage: 'Web Page',
    AudioBlog: 'Audio Blog',
    WebSearch: 'Web Search',
};
export const sourceTypeLabel = t => SOURCE_TYPE_LABELS[t] || t || '—';

export function sourceTypeBadge(t) {
    const v = String(t || '').toLowerCase();
    if (v === 'rss') return 'badge-orange';
    if (v === 'youtube' || v === 'youtubechannel' || v === 'video') return 'badge-red';
    if (v === 'podcast' || v === 'audioblog') return 'badge-purple';
    if (v === 'googlenews' || v === 'websearch') return 'badge-blue';
    if (v === 'manual') return 'badge-gray';
    return 'badge-green';
}

// ── Parsing ──────────────────────────────────────────────────────────────────
/** Parse a JSON array column, returning [] on anything unparseable. */
export function parseJsonArray(json) {
    if (!json) return [];
    try {
        const v = JSON.parse(json);
        return Array.isArray(v) ? v : [];
    } catch {
        return [];
    }
}

/** Parse the ImpactsJson dict: { perspectiveId: impactText }. */
export function parseImpacts(json) {
    if (!json) return {};
    try {
        const v = JSON.parse(json);
        return v && typeof v === 'object' && !Array.isArray(v) ? v : {};
    } catch {
        return {};
    }
}

/**
 * Impacts for display: resolved perspective names, sorted by perspective id,
 * with the model's "nothing to say here" placeholders dropped.
 */
export function displayImpacts(impactsJson, perspectiveNames) {
    const impacts = parseImpacts(impactsJson);
    return Object.keys(impacts)
        .sort()
        .filter(k => !String(impacts[k]).includes('No specific impact identified')
            && !String(impacts[k]).includes('No direct impact identified'))
        .map(k => ({
            perspectiveId: k,
            perspectiveName: perspectiveNames[k] || 'Perspective',
            text: impacts[k],
        }));
}

/**
 * Tags shown on a card. `thin-source` is an internal quality marker, not
 * something to put in front of a reader.
 */
export const displayTags = tagsJson => parseJsonArray(tagsJson).filter(t => t !== 'thin-source');

/** Top N tags across the loaded cards, excluding the ubiquitous ones. */
export function buildPopularTags(cards, limit = 9) {
    const exclude = new Set(['public-safety', 'uas-operations', 'thin-source']);
    const counts = new Map();
    cards.forEach(c => {
        parseJsonArray(c.tagsJson).forEach(t => {
            if (exclude.has(t)) return;
            counts.set(t, (counts.get(t) || 0) + 1);
        });
    });
    return [...counts.entries()]
        .sort((a, b) => b[1] - a[1])
        .slice(0, limit)
        .map(([tag]) => tag);
}

export function formatDuration(seconds) {
    const s = Number(seconds);
    if (!s) return null;
    const m = Math.floor(s / 60);
    const rem = s % 60;
    if (m >= 60) return `${Math.floor(m / 60)}h ${String(m % 60).padStart(2, '0')}m`;
    return `${m}:${String(rem).padStart(2, '0')}`;
}

/**
 * Card dates as legacy IntelFeed shows them: "MMM d, yyyy" in the user's local
 * time (IntelFeed.razor.cs FormatDate adds Auth.UserTime to the UTC ticks).
 * Returns '' for anything unparseable, as legacy's catch does.
 */
export function formatIntelDate(ticks) {
    const EPOCH_TICKS = 621355968000000000;
    const n = Number(ticks);
    if (!n) return '';
    const d = new Date((n - EPOCH_TICKS) / 10000);
    if (Number.isNaN(d.getTime())) return '';
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

/**
 * Scan cadence on a channel card (IntelAdmin.razor.cs FormatFrequency):
 * "6 hours" when unset, "X min" under an hour, else whole hours.
 */
export function formatFrequency(minutes) {
    const m = Number(minutes) || 0;
    if (m <= 0) return '6 hours';
    if (m < 60) return `${m} min`;
    return `${Math.floor(m / 60)} hours`;
}

/**
 * Source type from the URL pattern (IntelAdmin.razor.cs DetectSourceType).
 * The user's dropdown choice is returned only when nothing matches — the URL
 * knows better for the unambiguous hosts.
 */
export function detectSourceType(url, userSelected) {
    if (!url || !String(url).trim()) return userSelected;
    const lower = String(url).trim().toLowerCase();

    if (lower.includes('youtube.com/@') || lower.includes('youtube.com/channel/')
        || lower.includes('youtube.com/c/') || lower.includes('youtube.com/user/')) {
        return 'YouTubeChannel';
    }
    if (lower.includes('news.google.com/rss')) return 'GoogleNews';
    if (lower.includes('anchor.fm') || lower.includes('podcasts.apple.com')
        || lower.includes('feeds.buzzsprout.com') || lower.includes('feeds.libsyn.com')
        || lower.includes('feeds.megaphone.fm') || lower.includes('feeds.simplecast.com')
        || lower.includes('rss.art19.com') || lower.includes('podcast')) {
        return 'Podcast';
    }
    if (lower.endsWith('/feed') || lower.endsWith('/rss') || lower.endsWith('/atom.xml')
        || lower.endsWith('.rss') || lower.endsWith('.xml') || lower.includes('/feed/')) {
        return 'RSS';
    }
    return userSelected;
}

/** ContentFormat from source type (IntelAdmin.razor.cs InferContentFormat). */
export function inferContentFormat(sourceType) {
    if (sourceType === 'YouTubeChannel') return 'Video';
    if (sourceType === 'Podcast') return 'Audio';
    return 'Text';
}

/** Relative age of a card, e.g. "3h ago". Falls back to a date beyond a week. */
export function relativeAge(ticks) {
    const EPOCH_TICKS = 621355968000000000;
    const n = Number(ticks);
    if (!n) return '—';
    const then = new Date((n - EPOCH_TICKS) / 10000);
    if (Number.isNaN(then.getTime())) return '—';

    const mins = Math.floor((Date.now() - then.getTime()) / 60000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins}m ago`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    if (days < 7) return `${days}d ago`;
    return then.toLocaleDateString();
}
